import { ReactNode } from 'react';
import { DialogSizes } from 'carbon-react/lib/components/dialog/dialog.config';
import { IconType } from 'carbon-react/lib/components/icon/icon-type';

/** Represents the current screen state of a dialog wizard. */
export enum DialogWizardScreen {
  CONFIRM = 'confirm',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

interface DialogWizardSharedProps {
  /** Accessible name for the dialog when its visual title is not a plain string. */
  ariaLabel?: string;
  isOpen: boolean;
  onClose: () => void;
  screen: DialogWizardScreen;
  title: ReactNode;
  subtitle?: ReactNode;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  closeButtonText?: ReactNode;
  modalSize?: DialogSizes;
  dataTestId?: string;
}

/** @internal Base props for DialogWizardBase. Not intended for direct use. */
export interface DialogWizardBaseProps extends DialogWizardSharedProps {
  greyBackground?: boolean;
  children: ReactNode;
}

/** Props for ActionDialogWizard — a confirmation dialog with Cancel/Confirm buttons. */
export interface ActionDialogWizardProps extends DialogWizardSharedProps {
  onConfirm: () => void;
  confirmTitle?: ReactNode;
  confirmDescription?: ReactNode;
  confirmButtonText?: ReactNode;
  cancelButtonText?: ReactNode;
  /** When provided, renders an icon next to the title (same layout as DeleteDialogWizard). */
  icon?: { type: IconType; color: string };
  /** Optional list of "things to know" bullet items rendered inside a styled box. */
  thingsToKnow?: ReactNode[];
  /** Custom heading for the things-to-know section. Defaults to "Things to know:". */
  thingsToKnowTitle?: ReactNode;
}

/** Props for DeleteDialogWizard — a destructive confirmation dialog with Cancel/Delete buttons. */
export interface DeleteDialogWizardProps extends DialogWizardSharedProps {
  onDelete: () => void;
  deleteDescription?: ReactNode;
  /**
   * Optional list of "things to know" bullet items rendered inside a styled box
   * with a hardcoded "Things to know:" heading. Pass one ReactNode per bullet.
   */
  thingsToKnow?: ReactNode[];
  deleteButtonText?: ReactNode;
  cancelButtonText?: ReactNode;
}
